import type { VariableNode, VariablesGroup } from 'services/entities/VariablesEntity';
import { VariableDomain, makeFolderNode, hasObjectInstances } from 'services/entities/VariablesEntity';
import type { FlattenedOption } from './types';

export function formatPath(path: string[]): string {
  return path.length <= 2 ? path.join(' / ') : `${path[0]} / … / ${path.at(-1)}`;
}

export function flattenVariables(nodes: VariableNode[], parentPath: string[] = []): FlattenedOption[] {
  return nodes
    .flatMap((node) => {
      const currentPath = [...parentPath, node.friendlyName];

      const children: FlattenedOption[] = node.variables?.length ? flattenVariables(node.variables, currentPath) : [];

      const option: FlattenedOption = {
        path: node.path,
        label: node.friendlyName,
        friendlyName: node.friendlyName,
        value: node.value ?? null,
        name: node.name,
        variables: children.length ? children : [],
        objectId: node.objectId,
        shape: node.shape,
        type: node.type,
      };

      return [option, ...children];
    })
    .filter((node) => !node.variables?.length);
}

export const adjustDefaultVariables = (data: VariablesGroup | null, path: string): VariableNode[] | null => {
  if (data === null) return null;
  return Object.keys(data).map((key) => {
    const adjustedFriendlyName = `${key.charAt(0).toUpperCase() + key.slice(1)}`;

    const adjustedVariables: VariableNode[] = data[key].map((variable) => ({
      ...variable,
      friendlyName: variable.friendlyName.replace('/', '-'),
      name: variable.name ?? variable.friendlyName,
      variables: [],
      path: `${path}/${adjustedFriendlyName}/${variable.friendlyName.replace('/', '-')}`,
    }));

    return makeFolderNode({
      name: key,
      friendlyName: adjustedFriendlyName,
      path: `${path}/${adjustedFriendlyName}`,
      variables: adjustedVariables,
    });
  });
};

export const formatVariablesPath = (data: VariableNode[], defaultPath: string): VariableNode[] => {
  if (!data) return data;
  else
    return data.map((node) => {
      const currentPath = `${defaultPath}/${node.friendlyName}`;
      return {
        ...node,
        variables: node.variables && node.variables.length > 0 ? formatVariablesPath(node.variables, currentPath) : [],
        path: currentPath,
      };
    });
};

interface PrepareAllVariableDataParams {
  allPath: string;
  data: { variableName: string; variableData?: VariableNode[] | null }[];
}

export const prepareAllVariableData = ({ allPath, data }: PrepareAllVariableDataParams): VariableNode => {
  const assembleAllVariables: VariableNode[] = data.reduce<VariableNode[]>((acc, { variableData, variableName }) => {
    if (!variableData || variableData.length === 0) return acc;

    acc.push(
      makeFolderNode({
        name: variableName,
        friendlyName: variableName,
        path: `${allPath}/${variableName}`,
        variables: variableData,
      })
    );

    return acc;
  }, []);

  const root: VariableNode = makeFolderNode({
    name: allPath,
    friendlyName: allPath,
    path: '',
    variables: assembleAllVariables.length > 0 ? assembleAllVariables : undefined,
  });

  return root;
};

const INTEGRATIONS_ROOT_PATH = `All/${VariableDomain.INTEGRATIONS}`;
const LEGACY_CRM_VARIABLES_ROOT_PATH = /^All\/CRM Variables?(\/|$)/;

export function isIntegrationsFolderPath(path: string): boolean {
  if (path === INTEGRATIONS_ROOT_PATH || path.startsWith(`${INTEGRATIONS_ROOT_PATH}/`)) return true;
  return LEGACY_CRM_VARIABLES_ROOT_PATH.test(path);
}

/**
 * `grouped` prefers the wire value; v1 CRM arrays omit `grouped` until DT-1866, so it falls back
 * to structural derivation (`hasObjectInstances`) instead of defaulting to `false` — a flat
 * default mislabels every `.item`-structured v1 array and routes the paste through the wrong branch.
 */
export function toArrayClipboardNode(node: VariableNode): VariableNode {
  return {
    ...node,
    shape: { ...node.shape, type: 'array', grouped: node.shape?.grouped ?? hasObjectInstances(node) },
  };
}

export const stringifyNodeValue = (value: unknown): string | null => (value !== null && value !== undefined ? String(value) : null);

// A field-collection entry (built below from `fields`) is synthetic: it groups one scalar
// leaf's values across every sibling instance, and is always stamped `shape.grouped: true`
// regardless of the real field's nature (an unrelated paste-behavior convention). A real
// nested array entry (built from `nestedByTemplate`) instead spreads the actual child node
// as-is, so its `shape.grouped` is the genuine wire value. VariableFolders.tsx's
// isExpandableArray must distrust `grouped` only for the former -- flagged explicitly here
// so it doesn't have to guess structurally (both kinds can otherwise look identical: an
// array-shaped node whose own children are plain scalar leaves, e.g. in template mode).
type FieldCollectionEntry = VariableNode & { isFieldCollection: true };

export function isFieldCollectionEntry(node: VariableNode): boolean {
  return (node as Partial<FieldCollectionEntry>).isFieldCollection === true;
}

export function deriveArrayFieldSchema(arrayNode: VariableNode): VariableNode[] {
  const fields = new Map<string, VariableNode[]>();
  const nestedByTemplate = new Map<string, VariableNode[]>();

  const walk = (node: VariableNode) => {
    (node.variables ?? []).forEach((child) => {
      if (child.shape?.type === 'array') {
        const key = String(child.template ?? child.name);
        nestedByTemplate.set(key, [...(nestedByTemplate.get(key) ?? []), child]);
      } else if (child.variables?.length) {
        walk(child);
      } else if (child.path) {
        const key = String(child.template ?? child.name);
        fields.set(key, [...(fields.get(key) ?? []), child]);
      }
    });
  };
  walk(arrayNode);

  const out: VariableNode[] = [];

  fields.forEach((instances, template) => {
    const first = instances[0];
    const entry: FieldCollectionEntry = {
      name: template,
      template,
      friendlyName: first.friendlyName,
      value: stringifyNodeValue(first.value),
      shape: { type: 'array', grouped: true },
      path: `${arrayNode.path}/${String(first.friendlyName).replaceAll('/', '-')}`,
      variables: instances,
      isFieldCollection: true,
    };
    if (first.type !== undefined) entry.type = first.type;
    if (arrayNode.objectId !== undefined) entry.objectId = arrayNode.objectId;
    out.push(entry);
  });

  nestedByTemplate.forEach((copies) => {
    out.push({ ...copies[0], variables: copies.flatMap((copy) => copy.variables ?? []) });
  });

  return out;
}
